<script lang="ts">
  /**
   * Hotspotter Wrapped (amendment 26): your month as a story — a card at a time, a tap to go
   * on — that ends in a picture to share.
   *
   * Only ever your own month, counted on the server when this opens and kept nowhere. The
   * picture is drawn here, and the one thing on it that says where you go, the favourite cell,
   * stays off until you put it on: the preview is exactly what leaves the phone.
   */
  import { type Attribute, MAX_STARS } from '$domain/attributes.ts';
  import { badgeFor } from '$domain/badges.ts';
  import type { Persona, WrappedStats } from '$domain/wrapped.ts';
  import { untrack } from 'svelte';
  import { cubicOut } from 'svelte/easing';
  import { Tween } from 'svelte/motion';
  import { request } from './api.ts';
  import { gdiColour, gdiLabel } from './gdi-scale.ts';
  import { cachedPlace, reverseGeocode } from './place.ts';
  import { describeError } from './session.svelte.ts';
  import { sharePicture } from './share.ts';
  import { drawWrappedCard, type WrappedCard } from './wrapped-card.ts';

  type Props = { attributes: Attribute[]; onclose: () => void };
  const { attributes, onclose }: Props = $props();

  type Answer = { timeZone: string; months: string[]; stats: WrappedStats; persona: Persona };
  type Card =
    | 'intro'
    | 'reports'
    | 'favourite'
    | 'discover'
    | 'hottest'
    | 'attribute'
    | 'rhythm'
    | 'social'
    | 'coins'
    | 'persona'
    | 'share';

  /** Each card's colours, deep enough at both ends that white text reads anywhere on them. */
  const COLOURS: Record<Card, [string, string]> = {
    intro: ['#2b1055', '#4f46e5'],
    reports: ['#9f1239', '#ea580c'],
    favourite: ['#064e3b', '#059669'],
    discover: ['#1e3a8a', '#0891b2'],
    hottest: ['#7f1d1d', '#dc2626'],
    attribute: ['#581c87', '#c026d3'],
    rhythm: ['#0b1026', '#3730a3'],
    social: ['#831843', '#db2777'],
    coins: ['#713f12', '#b45309'],
    persona: ['#2b1055', '#a4206a'],
    share: ['#0f172a', '#2b1055'],
  };

  /** ISO weekdays, Monday first, the way "am liebsten …" wants them. */
  const WEEKDAYS = [
    'montags',
    'dienstags',
    'mittwochs',
    'donnerstags',
    'freitags',
    'samstags',
    'sonntags',
  ];

  const reduced =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** This month where the reader is: what the first answer is about. */
  const today = new Date();
  const thisMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

  let answer = $state<Answer | undefined>();
  /** The month asked for; `undefined` is this one. */
  let month = $state<string | undefined>();
  let loading = $state(true);
  let error = $state('');
  let index = $state(0);
  let names = $state<Record<string, string>>({});
  /** Whether the favourite cell's name goes on the picture. Off until asked for, every time. */
  let withPlace = $state(false);
  let sharing = $state(false);
  let shared = $state('');
  let root = $state<HTMLElement | undefined>();
  let holder = $state<HTMLElement | undefined>();
  /** The picture as previewed, which is the picture that is shared. */
  let picture: HTMLCanvasElement | undefined;
  /** Which answer is still wanted: a month picked twice in a row must not end on the first. */
  let asked = 0;

  $effect(() => {
    void load(month, ++asked);
  });

  async function load(wanted: string | undefined, ticket: number) {
    loading = true;
    const query = new URLSearchParams({ tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
    if (wanted !== undefined) query.set('month', wanted);
    try {
      const next = await request<Answer>(`/api/me/wrapped?${query}`);
      if (ticket !== asked) return;
      answer = next;
      error = '';
      void name([next.stats.favourite?.cell, next.stats.hottest?.cell]);
    } catch (cause) {
      if (ticket === asked) error = describeError(cause);
    } finally {
      if (ticket === asked) loading = false;
    }
  }

  /** Names for the cells on the cards — at most one Nominatim request a second. */
  async function name(cells: (string | undefined)[]) {
    for (const cell of cells) {
      if (cell === undefined || names[cell] !== undefined) continue;
      const known = cachedPlace(cell);
      if (known !== undefined) {
        names = { ...names, [cell]: known };
        continue;
      }
      const found = await reverseGeocode(cell);
      if (found !== undefined) names = { ...names, [cell]: found };
      await new Promise((resolve) => setTimeout(resolve, 1_100));
    }
  }

  const stats = $derived(answer?.stats);

  /** The cards this month has something for. Every month gets the type and the picture. */
  const cards = $derived.by<Card[]>(() => {
    if (stats === undefined) return ['intro'];
    const shown: Card[] = ['intro'];
    if (stats.reports > 0) shown.push('reports');
    if (stats.favourite !== null) shown.push('favourite');
    if (stats.newCells > 0 || stats.districts > 1) shown.push('discover');
    if (stats.hottest !== null) shown.push('hottest');
    if (stats.topAttribute !== null) shown.push('attribute');
    if (stats.weekday !== null && stats.hour !== null) shown.push('rhythm');
    if (stats.confirmations + stats.swipes + stats.messages + stats.rounds > 0) {
      shown.push('social');
    }
    if (stats.coinsEarned + stats.coinsSpent > 0 || stats.badges.length > 0) shown.push('coins');
    shown.push('persona', 'share');
    return shown;
  });
  const card = $derived(cards[Math.min(index, cards.length - 1)] ?? 'intro');

  function go(step: number) {
    const next = index + step;
    if (next >= 0 && next < cards.length) index = next;
  }

  function pick(entry: string) {
    if (entry === stats?.month) return;
    month = entry;
    index = 0;
    withPlace = false;
    shared = '';
  }

  /** 0 → 1 as a card comes in; every number on it counts up along with it. */
  const rise = new Tween(1, { duration: 900, easing: cubicOut });
  $effect(() => {
    const entering = card;
    if (reduced || entering === 'intro') return;
    untrack(() => void rise.set(0, { duration: 0 }).then(() => rise.set(1)));
  });
  const count = (value: number) => Math.round(value * rise.current).toLocaleString('de-DE');

  // Like the Umschau: the dialogue takes the keyboard while it is open.
  $effect(() => {
    root?.focus();
  });

  // The preview, drawn when the last card comes in and again whenever the place goes on or off.
  $effect(() => {
    const target = holder;
    if (card !== 'share' || target == null || answer === undefined) return;
    const drawn = pictureOf(answer, withPlace);
    let current = true;
    void drawWrappedCard(drawn).then((canvas) => {
      if (!current) return;
      canvas.setAttribute('role', 'img');
      canvas.setAttribute('aria-label', 'Vorschau des Bildes zum Teilen');
      picture = canvas;
      target.replaceChildren(canvas);
    });
    return () => {
      current = false;
    };
  });

  function pictureOf(shown: Answer, place: boolean): WrappedCard {
    const top = shown.stats.topAttribute;
    const attribute = attributes.find((entry) => entry.key === top?.key);
    const favourite = shown.stats.favourite?.cell;
    return {
      stats: shown.stats,
      persona: shown.persona,
      monthName: monthName(shown.stats.month, true),
      topAttribute:
        top === null || attribute === undefined
          ? undefined
          : `${attribute.emoji} ${attribute.label} · Ø ${decimal(top.mean)} ★`,
      badges: shown.stats.badges.flatMap((key) => badgeFor(key)?.emoji ?? []),
      place: place && favourite !== undefined ? names[favourite] : undefined,
    };
  }

  async function share() {
    if (picture === undefined || stats === undefined) return;
    sharing = true;
    shared = '';
    const outcome = await sharePicture(picture, `hotspotter-${stats.month}.png`);
    sharing = false;
    shared =
      outcome === 'copied'
        ? 'Bild in die Zwischenablage kopiert.'
        : outcome === 'unsupported'
          ? 'Teilen geht in diesem Browser nicht.'
          : '';
  }

  /** "Oktober" — with the year when it is not this one's, or when asked for. */
  function monthName(value: string, withYear = false): string {
    const [year = 1970, number = 1] = value.split('-').map(Number);
    const showYear = withYear || year !== today.getFullYear();
    return new Intl.DateTimeFormat('de-DE', {
      month: 'long',
      year: showYear ? 'numeric' : undefined,
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, number - 1, 15)));
  }

  /** "Freitag, 16. Oktober" for `2026-10-16`. */
  function dayName(day: string): string {
    return new Intl.DateTimeFormat('de-DE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC',
    }).format(new Date(`${day}T12:00:00Z`));
  }

  function clock(hour: number): { emoji: string; line: string } {
    if (hour >= 23 || hour < 5) return { emoji: '🌙', line: 'Wenn andere schlafen, meldest du.' };
    if (hour < 11) return { emoji: '🌅', line: 'Früh unterwegs – die Karte sagt danke.' };
    if (hour < 18) return { emoji: '☀️', line: 'Tagsüber unterwegs. Auch das ist Feldforschung.' };
    return { emoji: '🌆', line: 'Feierabend ist Meldezeit.' };
  }

  const decimal = (value: number) =>
    value.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
  const stars = (mean: number) => {
    const full = Math.round(mean);
    return `${'★'.repeat(full)}${'☆'.repeat(MAX_STARS - full)}`;
  };
</script>

<svelte:window
  onkeydown={(event) => {
    if (event.key === 'Escape') onclose();
    else if (event.key === 'ArrowRight') go(1);
    else if (event.key === 'ArrowLeft') go(-1);
  }}
/>

{#snippet switcher()}
  {#if answer !== undefined && answer.months.length > 1}
    <div class="months" role="group" aria-label="Monat wählen">
      {#each answer.months as entry (entry)}
        <button aria-pressed={entry === stats?.month} disabled={loading} onclick={() => pick(entry)}>
          {monthName(entry)}
        </button>
      {/each}
    </div>
  {/if}
{/snippet}

<div
  class="wrapped"
  role="dialog"
  aria-modal="true"
  aria-label="Hotspotter Wrapped"
  tabindex="-1"
  bind:this={root}
  style:--from={COLOURS[card][0]}
  style:--to={COLOURS[card][1]}
>
  <!-- Under everything: a tap on the left third goes back, anywhere else on. The cards above
       let taps through except on their own buttons. -->
  <button class="zone back" aria-label="Zurück" disabled={index === 0} onclick={() => go(-1)}
  ></button>
  <button
    class="zone next"
    aria-label="Weiter"
    disabled={index >= cards.length - 1}
    onclick={() => go(1)}
  ></button>

  <div class="top">
    <ol class="progress" aria-hidden="true">
      {#each cards as entry, at (entry)}
        <li class:seen={at <= index}></li>
      {/each}
    </ol>
    <button class="close" onclick={onclose} aria-label="Schließen">×</button>
  </div>

  {#key `${stats?.month}:${card}`}
    <div class="card">
      {#if card === 'intro' || stats === undefined}
        <p class="kicker">Hotspotter Wrapped</p>
        <h2 class="huge">Dein {monthName(month ?? stats?.month ?? thisMonth)}</h2>
        {#if error}
          <p class="line" role="alert">{error}</p>
        {:else if loading}
          <p class="line">Einen Moment …</p>
        {:else}
          <p class="line">
            {stats?.month === answer?.months[0]
              ? 'Bis heute – der Monat läuft noch.'
              : 'Der ganze Monat auf einen Blick.'}
          </p>
          <p class="aside">Tippen zum Weiterblättern →</p>
        {/if}
        {@render switcher()}
      {:else if card === 'reports'}
        <p class="kicker">Gemeldet</p>
        <p class="number">{count(stats.reports)}</p>
        <p class="big">{plural(stats.reports, 'Meldung', 'Meldungen')}</p>
        <p class="line">
          an {stats.days}
          {plural(stats.days, 'Tag', 'Tagen')}, mit {stats.ratings}
          {plural(stats.ratings, 'Bewertung', 'Bewertungen')}.
        </p>
        <p class="aside">
          {stats.reports >= 30
            ? 'Ohne dich wäre die Karte ganz schön leer.'
            : stats.reports >= 10
              ? 'Damit lebt die Karte.'
              : 'Jede Meldung zählt.'}
        </p>
      {:else if card === 'favourite' && stats.favourite !== null}
        <p class="kicker">Deine Stammwabe</p>
        <p class="emoji">📍</p>
        <p class="big">{names[stats.favourite.cell] ?? 'Eine Wabe, die du gut kennst'}</p>
        <p class="line">{stats.favourite.reports}-mal hast du hier gemeldet.</p>
        <p class="aside">Bleibt unter uns: Aufs Bild kommt sie nur, wenn du es willst.</p>
      {:else if card === 'discover'}
        <p class="kicker">Neuland</p>
        {#if stats.newCells > 0}
          <p class="number">{count(stats.newCells)}</p>
          <p class="big">
            {plural(
              stats.newCells,
              'Wabe, in der vor dir noch niemand gemeldet hatte',
              'Waben, in denen vor dir noch niemand gemeldet hatte',
            )}
          </p>
        {:else}
          <p class="emoji">🧭</p>
        {/if}
        {#if stats.districts > 0}
          <p class="line">
            Unterwegs in {stats.districts}
            {plural(stats.districts, 'Viertel', 'Vierteln')}.
          </p>
        {/if}
        <p class="aside">
          {stats.newCells >= 3 ? 'Kolumbus wäre neidisch.' : 'Wer sucht, der findet.'}
        </p>
      {:else if card === 'hottest' && stats.hottest !== null}
        {@const where = names[stats.hottest.cell]}
        <p class="kicker">Dein heißester Moment</p>
        <p class="number">{count(stats.hottest.gdi)}</p>
        <p class="big">
          <!-- Outlined, because the hottest colours are the colours of this card. -->
          <svg class="swatch" viewBox="0 0 20 23" aria-hidden="true">
            <polygon
              points="10,1.5 18.5,6.25 18.5,16.75 10,21.5 1.5,16.75 1.5,6.25"
              fill={gdiColour(stats.hottest.gdi)}
              stroke="#fff"
              stroke-width="2"
            />
          </svg>
          {gdiLabel(stats.hottest.gdi)}
        </p>
        <p class="line">{dayName(stats.hottest.day)}{where === undefined ? '' : ` · ${where}`}</p>
        <p class="aside">
          So voll war es in einer Wabe, aus der du gemeldet hast – Geschicktendichte-Index, 0 bis
          100.
        </p>
      {:else if card === 'attribute' && stats.topAttribute !== null}
        {@const top = stats.topAttribute}
        {@const attribute = attributes.find((entry) => entry.key === top.key)}
        <p class="kicker">Am meisten bewertet</p>
        <p class="emoji">{attribute?.emoji ?? '⭐'}</p>
        <p class="big">{attribute?.label ?? top.key}</p>
        <p class="line">{top.count}-mal, im Schnitt {decimal(top.mean)} von {MAX_STARS} Sternen.</p>
        <p class="stars" aria-hidden="true">{stars(top.mean)}</p>
      {:else if card === 'rhythm' && stats.hour !== null && stats.weekday !== null}
        {@const time = clock(stats.hour)}
        <p class="kicker">Dein Rhythmus</p>
        <p class="emoji">{time.emoji}</p>
        <p class="big">Am liebsten {WEEKDAYS[stats.weekday - 1]} um {stats.hour} Uhr</p>
        {#if stats.nightReports > 0}
          <p class="line">
            {stats.nightReports}
            {plural(stats.nightReports, 'Meldung', 'Meldungen')} nach 23 Uhr.
          </p>
        {/if}
        <p class="aside">{time.line}</p>
      {:else if card === 'social'}
        <p class="kicker">Mit den anderen</p>
        <ul class="tiles">
          {#if stats.confirmations > 0}
            <li>
              <strong>{count(stats.confirmations)}</strong>
              {plural(stats.confirmations, 'Meldung anderer geprüft', 'Meldungen anderer geprüft')}
            </li>
          {/if}
          {#if stats.swipes > 0}
            <li>
              <strong>{count(stats.swipes)}</strong>
              {plural(stats.swipes, 'Ort im Vorbeigehen beurteilt', 'Orte im Vorbeigehen beurteilt')}
            </li>
          {/if}
          {#if stats.messages > 0}
            <li>
              <strong>{count(stats.messages)}</strong>
              {plural(stats.messages, 'Nachricht im Zonenchat', 'Nachrichten im Zonenchat')}
            </li>
          {/if}
          {#if stats.rounds > 0}
            <li>
              <strong>{count(stats.rounds)}</strong>
              {plural(stats.rounds, 'Runde Trinkspiel', 'Runden Trinkspiele')}
            </li>
          {/if}
        </ul>
        <p class="aside">Allein ist die Karte nur halb so lustig.</p>
      {:else if card === 'coins'}
        <p class="kicker">Coins und Abzeichen</p>
        {#if stats.coinsEarned > 0}
          <p class="number">+{count(stats.coinsEarned)}</p>
          <p class="big">🪙 Hotspotter-Coins verdient</p>
        {/if}
        {#if stats.coinsEarned + stats.coinsSpent > 0}
          <p class="line">
            {stats.coinsSpent > 0
              ? `${stats.coinsSpent.toLocaleString('de-DE')} 🪙 ausgegeben – fürs gute Aussehen.`
              : 'Noch nichts ausgegeben. Im Laden gibt es übrigens einen Fliegerhut.'}
          </p>
        {/if}
        {#if stats.badges.length > 0}
          <p class="line">
            {plural(stats.badges.length, 'Ein neues Abzeichen:', `${stats.badges.length} neue Abzeichen:`)}
          </p>
          <ul class="badges">
            {#each stats.badges as key (key)}
              {@const badge = badgeFor(key)}
              {#if badge !== undefined}
                <li><span>{badge.emoji}</span> {badge.label}</li>
              {/if}
            {/each}
          </ul>
        {/if}
      {:else if card === 'persona' && answer !== undefined}
        <p class="kicker">Im {monthName(stats.month)} warst du</p>
        <p class="emoji">{answer.persona.emoji}</p>
        <h2 class="huge">{answer.persona.title}</h2>
        <p class="line">{answer.persona.line}</p>
      {:else if card === 'share'}
        <p class="kicker">Dein {monthName(stats.month)} als Bild</p>
        <div class="preview" bind:this={holder}></div>
        {#if stats.favourite !== null && names[stats.favourite.cell] !== undefined}
          <label class="toggle">
            <input type="checkbox" bind:checked={withPlace} />
            📍 Stammwabe aufs Bild
          </label>
        {/if}
        <button class="share" disabled={sharing} onclick={share}>Teilen</button>
        {#if shared}<p class="aside" role="status">{shared}</p>{/if}
        {@render switcher()}
      {/if}
    </div>
  {/key}
</div>

<style>
  /* Registered, so that the background can glide from one card's colours to the next. */
  @property --from {
    syntax: '<color>';
    inherits: true;
    initial-value: #2b1055;
  }

  @property --to {
    syntax: '<color>';
    inherits: true;
    initial-value: #4f46e5;
  }

  .wrapped {
    position: fixed;
    inset: 0;
    z-index: 30;
    display: flex;
    flex-direction: column;
    /* Clip, not hidden: the hexagons reach past the edges, and a `hidden` box is still a scroll
       container — focusing the checkbox scrolled the whole story up and the close button off. */
    overflow: clip;
    padding: max(0.75rem, env(safe-area-inset-top)) 1.25rem max(1rem, env(safe-area-inset-bottom));
    background: linear-gradient(160deg, var(--from), var(--to));
    color: #fff;
    transition:
      --from 0.6s ease,
      --to 0.6s ease;
    user-select: none;
    -webkit-user-select: none;
  }

  .wrapped:focus {
    outline: none;
  }

  /* Two hexagons drifting behind everything, faintly: the app's own shape. */
  .wrapped::before,
  .wrapped::after {
    content: '';
    position: absolute;
    z-index: -1;
    background: rgb(255 255 255 / 7%);
    clip-path: polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%);
    pointer-events: none;
    animation: drift 14s ease-in-out infinite alternate;
  }

  .wrapped::before {
    top: -6rem;
    right: -7rem;
    width: 20rem;
    height: 23rem;
  }

  .wrapped::after {
    bottom: -3rem;
    left: -4rem;
    width: 12rem;
    height: 13.8rem;
    animation-duration: 18s;
  }

  @keyframes drift {
    to {
      transform: translate(-1.5rem, 2rem) rotate(12deg);
    }
  }

  .zone {
    position: absolute;
    top: 0;
    bottom: 0;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .zone:disabled {
    cursor: default;
  }

  .zone:focus-visible {
    outline: 2px solid rgb(255 255 255 / 60%);
    outline-offset: -6px;
  }

  .back {
    left: 0;
    width: 30%;
  }

  .next {
    right: 0;
    width: 70%;
  }

  .top {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    pointer-events: none;
  }

  .progress {
    display: flex;
    flex: 1;
    gap: 0.25rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .progress li {
    flex: 1;
    height: 0.2rem;
    border-radius: 1rem;
    background: rgb(255 255 255 / 30%);
    transition: background 0.3s;
  }

  .progress li.seen {
    background: #fff;
  }

  .close {
    padding: 0 0.25rem;
    border: 0;
    background: none;
    color: inherit;
    font-size: 2rem;
    line-height: 1;
    cursor: pointer;
    pointer-events: auto;
  }

  .card {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: safe center;
    gap: 0.8rem;
    width: 100%;
    max-width: 28rem;
    min-height: 0;
    margin-inline: auto;
    padding-block: 1rem;
    pointer-events: none;
  }

  .card :is(button, label, input) {
    pointer-events: auto;
  }

  .card p,
  .card h2 {
    margin: 0;
  }

  .kicker {
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    opacity: 0.8;
  }

  .number {
    font-size: clamp(4.5rem, 24vw, 7.5rem);
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    letter-spacing: -0.03em;
  }

  .huge {
    font-size: clamp(2.2rem, 11vw, 3.4rem);
    font-weight: 800;
    line-height: 1.05;
  }

  .big {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }

  .line {
    font-size: 1.05rem;
    line-height: 1.4;
  }

  .aside {
    font-size: 0.9rem;
    opacity: 0.75;
  }

  .emoji {
    font-size: 4.5rem;
    line-height: 1;
  }

  .stars {
    font-size: 1.75rem;
    letter-spacing: 0.15em;
  }

  .swatch {
    width: 1.1rem;
    height: 1.25rem;
    margin-right: 0.2rem;
    vertical-align: -0.1rem;
  }

  .tiles {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .tiles li {
    display: grid;
    align-content: start;
    gap: 0.25rem;
    padding: 0.8rem;
    border-radius: 1rem;
    background: rgb(255 255 255 / 12%);
    font-size: 0.85rem;
    line-height: 1.3;
  }

  .tiles strong {
    font-size: 2rem;
    font-weight: 800;
    line-height: 1;
  }

  .badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .badges li {
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    background: rgb(255 255 255 / 14%);
    font-size: 0.9rem;
  }

  .months {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  .months button {
    padding: 0.4rem 0.85rem;
    border: 1px solid rgb(255 255 255 / 45%);
    border-radius: 999px;
    background: none;
    color: inherit;
    font: inherit;
    font-size: 0.85rem;
    cursor: pointer;
  }

  .months button[aria-pressed='true'] {
    background: #fff;
    color: #2b1055;
    font-weight: 700;
  }

  .preview {
    display: flex;
    justify-content: center;
  }

  /* The canvas is put in by hand, so it carries no scoping class. Its own 4:5 survives both
     limits, because a canvas is a replaced element. */
  .preview :global(canvas) {
    max-width: 100%;
    max-height: min(44dvh, 26rem);
    border-radius: 1rem;
    box-shadow: 0 12px 40px rgb(0 0 0 / 35%);
  }

  .toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.95rem;
    cursor: pointer;
  }

  .toggle input {
    width: 1.1rem;
    height: 1.1rem;
    accent-color: #fff;
  }

  .share {
    padding: 0.8rem 1rem;
    border: 0;
    border-radius: 999px;
    background: #fff;
    color: #2b1055;
    font: inherit;
    font-size: 1rem;
    font-weight: 800;
    cursor: pointer;
  }

  .share:disabled {
    opacity: 0.6;
  }

  /* Each card comes in line by line; its emoji pops. */
  .card > * {
    animation: rise 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  }

  .card > :nth-child(2) {
    animation-delay: 0.08s;
  }

  .card > :nth-child(3) {
    animation-delay: 0.16s;
  }

  .card > :nth-child(4) {
    animation-delay: 0.24s;
  }

  .card > :nth-child(n + 5) {
    animation-delay: 0.32s;
  }

  .card > .emoji {
    animation-name: pop;
    animation-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  @keyframes rise {
    from {
      opacity: 0;
      transform: translateY(1.25rem);
    }
  }

  @keyframes pop {
    from {
      opacity: 0;
      transform: scale(0.3) rotate(-12deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .wrapped {
      transition: none;
    }

    .wrapped::before,
    .wrapped::after,
    .card > * {
      animation: none;
    }
  }
</style>
